import { Box, Typography } from "@mui/material";
import { useState } from "react";

import {
  AgentPerformanceData,
  RiskAnalysisData
} from "../../../../types/risk-analysis";
import EvidenceList from "./EvidenceList";
import ExpandIcon from "./ExpandIcon";

interface SummaryFooterProps {
  riskAnalysis: RiskAnalysisData;
  agentPerformance?: AgentPerformanceData;
}

const SummaryFooter = ({
  riskAnalysis,
  agentPerformance
}: SummaryFooterProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);

  const showAgentPerformance = Boolean(agentPerformance?.summary);
  const footerLabel = showAgentPerformance
    ? "Interaction's Impact on Risk"
    : "Summary Statement";

  return (
    <Box
      onClick={() => setIsExpanded((prev) => !prev)}
      sx={{
        cursor: "pointer",
        borderTop: "1px solid #e5e7eb",
        p: "12px 24px",
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: "#f9fafb",
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)"
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "14px",
          fontWeight: 500,
          color: "#374151"
        }}
      >
        <Typography component="span" sx={{ fontSize: "14px", fontWeight: 500 }}>
          {footerLabel}
        </Typography>
        <ExpandIcon isExpanded={isExpanded} sx={{ fontSize: 18 }} />
      </Box>

      {isExpanded && !showAgentPerformance && (
        <Typography
          sx={{
            fontSize: "14px",
            color: "#374151",
            lineHeight: 1.5,
            mt: 1.5,
            pt: 1.5,
            borderTop: "1px solid #e5e7eb"
          }}
        >
          {riskAnalysis.overallJustification}
        </Typography>
      )}

      {isExpanded && showAgentPerformance && agentPerformance && (
        <>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#374151",
              lineHeight: 1.5,
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px solid #e5e7eb"
            }}
          >
            {agentPerformance.summary}
          </Typography>

          {agentPerformance.afterCallRisk.evidence.length > 0 && (
            <Box
              sx={{ mt: 2, cursor: "pointer" }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setIsEvidenceExpanded((prev) => !prev);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: isEvidenceExpanded ? 1.5 : 0
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "inherit",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}
                >
                  Evidence
                </Typography>
                <ExpandIcon isExpanded={isEvidenceExpanded} sx={{ fontSize: 16 }} />
              </Box>
              {isEvidenceExpanded && (
                <Box sx={{ mt: 1.5 }}>
                  <EvidenceList
                    items={agentPerformance.afterCallRisk.evidence}
                    bulletColor="#3b82f6"
                  />
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SummaryFooter;
